import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { restaurantSession } from "@/lib/api";
import { BrandLogo } from "./Shared";
import {
  LayoutDashboard,
  ClipboardList,
  ChefHat,
  UtensilsCrossed,
  Layers,
  MessageCircle,
  Gift,
  BarChart3,
  Table2,
  Award,
  Settings,
  LogOut,
  Menu as MenuIcon,
  X,
  Receipt,
  History,
  Users,
  LifeBuoy,
} from "lucide-react";

const NAV = [
  { to: "/restaurant/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/restaurant/orders", label: "Live Orders", icon: ClipboardList },
  { to: "/restaurant/kitchen", label: "Kitchen (KDS)", icon: ChefHat },
  { to: "/restaurant/order-history", label: "Order History", icon: History },
  { to: "/restaurant/customers", label: "Customers", icon: Users },
  { to: "/restaurant/menu", label: "Menu Manager", icon: UtensilsCrossed },
  { to: "/restaurant/charges", label: "Custom Charges", icon: Receipt },
  { to: "/restaurant/page-tabs", label: "Page Tabs", icon: Layers },
  { to: "/restaurant/reviews", label: "Reviews", icon: MessageCircle },
  { to: "/restaurant/offers", label: "Offers", icon: Gift },
  { to: "/restaurant/analytics", label: "Analytics", icon: BarChart3 },
  { to: "/restaurant/tables", label: "Tables & QR", icon: Table2 },
  { to: "/restaurant/loyalty", label: "Loyalty", icon: Award },
  { to: "/restaurant/settings", label: "Settings", icon: Settings },
];

export const RestaurantLayout = ({ children }) => {
  const nav = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = React.useState(false);
  const session = restaurantSession.get();

  React.useEffect(() => {
    if (!session) nav("/restaurant/login");
    // eslint-disable-next-line
  }, []);

  const logout = () => {
    restaurantSession.clear();
    nav("/restaurant/login");
  };

  if (!session) return null;

  return (
    <div className="min-h-screen flex bg-[#F3EFEA] font-body">
      {/* Sidebar (desktop) */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-brand-dark text-brand-light flex-col transition-transform ${
          open ? "flex translate-x-0" : "hidden lg:flex"
        }`}
      >
        <div className="p-5 flex items-center justify-between">
          <div className="font-heading font-bold text-xl">
            <span className="text-brand-light">tab</span>
            <span className="text-brand-orange">dine</span>
            <div className="text-[10px] uppercase tracking-widest text-brand-light/50 mt-1">
              Restaurant POS
            </div>
          </div>
          <button
            className="lg:hidden text-brand-light"
            onClick={() => setOpen(false)}
            data-testid="close-sidebar"
          >
            <X size={20} />
          </button>
        </div>
        <div className="px-4 pb-3 text-xs text-brand-light/70 truncate">
          {session.restaurant?.name}
        </div>
        <nav className="flex-1 overflow-y-auto px-2">
          {NAV.map((n) => {
            const active = loc.pathname === n.to;
            const Icon = n.icon;
            return (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.to.split("/").pop()}`}
                onClick={() => setOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 my-0.5 rounded-md text-sm transition-colors ${
                  active
                    ? "bg-brand-orange text-white font-medium"
                    : "text-brand-light/80 hover:bg-white/5"
                }`}
              >
                <Icon size={16} strokeWidth={1.5} />
                {n.label}
              </Link>
            );
          })}
        </nav>
        <button
          onClick={logout}
          data-testid="logout-btn"
          className="m-3 mt-1 flex items-center gap-2 px-3 py-2 rounded-md text-sm text-brand-light/80 hover:bg-white/5"
        >
          <LogOut size={16} strokeWidth={1.5} /> Sign out
        </button>
        <a
          href="mailto:support.tabdine@gmail.com?subject=Tabdine%20Support%20Request"
          data-testid="support-link"
          className="mx-3 mb-3 flex items-center gap-2 px-3 py-2 rounded-md text-xs text-brand-light/60 hover:bg-white/5 border border-white/5"
        >
          <LifeBuoy size={14} strokeWidth={1.5} /> Support — support.tabdine@gmail.com
        </a>
      </aside>

      <div className="flex-1 min-w-0 flex flex-col">
        {/* Top bar */}
        <header className="lg:hidden sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-brand-dark/10 px-4 py-3 flex items-center justify-between">
          <button
            onClick={() => setOpen(true)}
            className="p-1"
            data-testid="open-sidebar"
          >
            <MenuIcon size={22} />
          </button>
          <BrandLogo small />
          <div className="w-6" />
        </header>
        <main className="flex-1 overflow-x-hidden">{children}</main>
      </div>
    </div>
  );
};
